/**
 * User UI preferences — server-backed so the same account matches across browsers/devices.
 * localStorage is a cache only.
 */
import api from './api';

export const DEFAULT_SCHEDULE_SETTINGS = {
  autoAddSchedule: true,
  askBeforeAdding: true,
  suppressPopupDays: 0,
  defaultView: 'month',
  showTodoList: true,
  todoDaysThreshold: 60,
  dontShowToday: true,
};

const LS_SCHEDULE = 'scheduleSettings';
const LS_TODO_DISMISS = 'todoListDismissedDate';

function readLocalSchedule() {
  try {
    const raw = localStorage.getItem(LS_SCHEDULE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeLocalSchedule(settings) {
  try {
    localStorage.setItem(LS_SCHEDULE, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event('scheduleSettingsChanged'));
}

function writeLocalTodoDismiss(dateStr) {
  try {
    if (dateStr) localStorage.setItem(LS_TODO_DISMISS, dateStr);
    else localStorage.removeItem(LS_TODO_DISMISS);
  } catch {
    /* ignore */
  }
}

export function getCachedScheduleSettings() {
  return { ...DEFAULT_SCHEDULE_SETTINGS, ...(readLocalSchedule() || {}) };
}

export function getCachedTodoDismissedDate() {
  try {
    return localStorage.getItem(LS_TODO_DISMISS) || null;
  } catch {
    return null;
  }
}

/** Load from server; fall back to local cache. Merges into localStorage. */
export async function loadUserPreferences() {
  const localSchedule = getCachedScheduleSettings();
  const localDismiss = getCachedTodoDismissedDate();
  try {
    const res = await api.get('/auth/preferences', { skipErrorToast: true });
    const data = res.data?.data || res.data || {};
    const persisted = !!data.persisted;
    let scheduleSettings = {
      ...DEFAULT_SCHEDULE_SETTINGS,
      ...(data.scheduleSettings && typeof data.scheduleSettings === 'object'
        ? data.scheduleSettings
        : {}),
    };
    let dismissed =
      data.todoListDismissedDate != null && data.todoListDismissedDate !== ''
        ? String(data.todoListDismissedDate)
        : null;

    // First login on this account: push this browser's local prefs to the server
    if (!persisted) {
      scheduleSettings = localSchedule;
      dismissed = localDismiss;
      try {
        await api.put(
          '/auth/preferences',
          { scheduleSettings, todoListDismissedDate: dismissed },
          { skipErrorToast: true }
        );
      } catch {
        /* ignore */
      }
    }

    writeLocalSchedule(scheduleSettings);
    if (dismissed) writeLocalTodoDismiss(dismissed);
    return {
      scheduleSettings,
      todoListDismissedDate: dismissed,
    };
  } catch {
    return {
      scheduleSettings: localSchedule,
      todoListDismissedDate: localDismiss,
    };
  }
}

/** Persist schedule settings to server + local cache. */
export async function saveScheduleSettings(partialOrFull) {
  const next = { ...getCachedScheduleSettings(), ...partialOrFull };
  writeLocalSchedule(next);
  try {
    await api.put('/auth/preferences', { scheduleSettings: next }, { skipErrorToast: true });
  } catch {
    /* keep local cache; other devices update on next successful save */
  }
  return next;
}

/** Persist "don't show todo today" across devices. */
export async function saveTodoDismissedDate(dateStr) {
  writeLocalTodoDismiss(dateStr);
  try {
    await api.put(
      '/auth/preferences',
      { todoListDismissedDate: dateStr },
      { skipErrorToast: true }
    );
  } catch {
    /* ignore */
  }
}
